import { FC, useEffect, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";

import { Input, Label, Button, CountrySelect, TextArea } from "../../common";
import { FormErrorMessage } from "../../messages";
import { SUPPORT_EMAIL } from "../../../constants";
import { selectCurrentUser } from "../../../store/reducers/user";
import { useUpdateInvoiceMutation } from "../../../store/services/invoices";
import type { Invoice, Party } from "../../../types/InvoiceTypes";
import { AccountType } from "../../../types/UserTypes";

type Props = {
  side: "from" | "to";
  invoice: Invoice;
  onSuccess?: () => void;
  onChange?: () => void;
};

type PartyType = AccountType;

// Global declarations
const defaultData: Party = {
  name: "",
  email: "",
  address: "",
  country: "",
  city: "",
  reg_number: "",
  tax_id: "",
  postal_code: "",
};

const labels: Record<PartyType, Record<string, string>> = {
  [AccountType.CONTRACTOR]: {
    companyNameFieldName: "company_name",
    companyNameFieldLabel: "Company nickname",
    companyNameFieldNote:
      "Your personal name will still pop up as sole proprietorship.",
    regNumberFieldNote:
      "Business registration number, in some countries it matches Tax ID number. If you work as individual fill up the field with your personal ID number.",
    taxIdFieldNote:
      "Unique identifier for tax purposes. You can find your Tax ID on official tax documents. In some countries it matches your regular ID number.",
    addressFieldNote:
      "Your residential address in the country you pay taxes in.",
  },
  [AccountType.BUSINESS]: {
    companyNameFieldName: "name",
    companyNameFieldLabel: "Client name",
    companyNameFieldNote: "",
    regNumberFieldNote:
      "Business registration number, in some countries it matches Tax ID number.",
    taxIdFieldNote: "Unique identifier for tax purposes.",
    addressFieldNote: "",
  },
};

const getVerifiedPostfix = (isVerified: boolean) =>
  isVerified ? <span className="absolute -left-16">Verified</span> : undefined;

const getVerifiedPostfixAddress = (isVerified: boolean) =>
  isVerified ? (
    <span className="absolute -left-10 -top-10">Verified</span>
  ) : undefined;
// End of global declarations

export const ClientDetailsForm: FC<Props> = ({
  side,
  invoice,
  onSuccess,
  onChange,
}) => {
  const { kyc_complete_and_approved, account_type } =
    useSelector(selectCurrentUser);

  const { user_is_issuer, user_is_payer, issuer_data, payer_data } = invoice;

  const data = (side === "from" ? issuer_data : payer_data) || defaultData;

  const {
    handleSubmit,
    control,
    formState: { isDirty, isValid },
    watch,
  } = useForm<Party>({
    defaultValues: data,
  });

  const [updateInvoice, { isSuccess, isError, error }] =
    useUpdateInvoiceMutation();

  useEffect(() => {
    if (isSuccess && onSuccess) {
      onSuccess();
    }
  }, [isSuccess, onSuccess]);

  useEffect(() => {
    const subscription = watch((data) => {
      onChange && onChange();
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [onChange, watch]);

  const onSubmit = useCallback(
    async (data: Party) => {
      const field = side === "from" ? "issuer_data" : "payer_data";

      await updateInvoice({
        id: invoice.id,
        [field]: data,
      });
    },
    [invoice.id, side, updateInvoice]
  );

  const isUserData =
    (side === "from" && user_is_issuer) ||
    (side === "to" && user_is_payer) ||
    false;

  // This is for UI rendering (labels, fields). If the party does not have an
  // account type yet, it will take the account type from the user's account
  // (if we are changing the user data) or it will defaulted to the type
  // depending on the side of the invoice ('from' or 'to').
  const partyType: PartyType =
    data.account_type || isUserData
      ? account_type
      : side === "to"
        ? AccountType.BUSINESS
        : AccountType.CONTRACTOR;

  const {
    companyNameFieldName,
    companyNameFieldLabel,
    companyNameFieldNote,
    regNumberFieldNote,
    taxIdFieldNote,
    addressFieldNote,
  } = labels[partyType];

  const footerText = isUserData
    ? "The invoice details will be saved as default in your settings"
    : ""; // TODO: "You can edit it in Clients"

  const isVerifiedCompanyAccount =
    isUserData &&
    partyType === AccountType.BUSINESS &&
    kyc_complete_and_approved;

  const verifiedPostfix = getVerifiedPostfix(isVerifiedCompanyAccount);

  const verifiedPostfixAddress = getVerifiedPostfixAddress(
    isVerifiedCompanyAccount
  );

  return (
    <form className="flex flex-col gap-8" onSubmit={handleSubmit(onSubmit)}>
      {side === "to" && (
        <Label label="Email">
          <Controller
            name="email"
            control={control}
            rules={{ required: false }}
            render={({ field }) => (
              <Input
                {...field}
                type="text"
                maxLength="100"
                note="Optional. We will only send invoices to that email when they are ready"
              />
            )}
          />
        </Label>
      )}

      <Label label={companyNameFieldLabel}>
        <Controller
          name={companyNameFieldName as "name" | "company_name"}
          control={control}
          rules={{ required: partyType === AccountType.BUSINESS }}
          render={({ field }) => (
            <Input
              {...field}
              type="text"
              maxLength="100"
              note={companyNameFieldNote}
              error={
                (error as any)?.data?.invoicing_data
                  ? (error as any)?.data?.invoicing_data[companyNameFieldName]
                  : null
              }
              disabled={isVerifiedCompanyAccount}
              postfix={verifiedPostfix}
            />
          )}
        />
      </Label>

      <Label label="Reg. №">
        <Controller
          name="reg_number"
          control={control}
          rules={{ required: partyType === AccountType.BUSINESS }}
          render={({ field }) => (
            <Input
              {...field}
              type="text"
              maxLength="20"
              note={
                partyType === AccountType.CONTRACTOR
                  ? `Optional. ${regNumberFieldNote}`
                  : regNumberFieldNote
              }
              disabled={isVerifiedCompanyAccount}
              postfix={verifiedPostfix}
            />
          )}
        />
      </Label>

      {isVerifiedCompanyAccount && (
        <div className="flex justify-between">
          <div className="theme-subheading">
            To change name after KYC please <br />
            contact customer support
          </div>

          <Button
            variant={Button.variant.SECONDARY}
            component={NavLink}
            to={`mailto:${SUPPORT_EMAIL}`}
          >
            Contact support
          </Button>
        </div>
      )}

      <Label label="Tax ID">
        <Controller
          name="tax_id"
          control={control}
          rules={{ required: partyType === AccountType.BUSINESS }}
          render={({ field }) => (
            <Input
              {...field}
              type="text"
              maxLength="20"
              note={
                partyType === AccountType.CONTRACTOR
                  ? `Optional. ${taxIdFieldNote}`
                  : taxIdFieldNote
              }
            />
          )}
        />
      </Label>

      <Label label="Address">
        <Controller
          name="address"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextArea
              {...field}
              rows={3}
              note={addressFieldNote}
              maxLength="100"
              disabled={isVerifiedCompanyAccount}
              postfix={verifiedPostfixAddress}
              className={isVerifiedCompanyAccount ? "pr-28" : ""}
            />
          )}
        />
      </Label>

      {!isVerifiedCompanyAccount && (
        <>
          <Label label="Postal code">
            <Controller
              name="postal_code"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input {...field} type="text" maxLength="20" />
              )}
            />
          </Label>

          <Label label="City">
            <Controller
              name="city"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input {...field} type="text" maxLength="50" />
              )}
            />
          </Label>
        </>
      )}

      <Label label="Country">
        <Controller
          name="country"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <CountrySelect
              {...field}
              disabled={isVerifiedCompanyAccount}
              postfix={verifiedPostfix}
              excludeBlacklisted={side === "from"}
            />
          )}
        />
      </Label>

      <FormErrorMessage isError={isError} error={error} />

      <Button
        type="submit"
        variant={Button.variant.PRIMARY}
        className="w-full"
        disabled={!isDirty || !isValid}
      >
        Save
      </Button>

      <div className="text-sm	text-gray-300 text-center">{footerText}</div>
    </form>
  );
};
